import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle, XCircle, Clock, ClipboardCheck } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import GradeBreakdownInput, { GradeEntry } from './GradeBreakdownInput';

export interface QualityInspectionData {
  sampleSizeChecked: boolean;
  sampleSizeNotes: string;
  damageKg: number;
  compensationKg: number;
  damagePercentage: number;
  damageNotes: string;
  qualityGrade: string;
  rejectionReason: string;
  gradeBreakdown: GradeEntry[];
  gradingType: 'preliminary' | 'final';
  finalGradingExpectedDate: string;
  preliminaryNotes: string;
}

interface QualityInspectionFormProps {
  inspectionData: QualityInspectionData;
  setInspectionData: React.Dispatch<React.SetStateAction<QualityInspectionData>>;
  onApprove: () => void;
  onReject: () => void;
  isSubmitting: boolean;
  expectedQuantity?: number;
}

const QUALITY_REJECTION_REASONS = {
  high_damage: 'High damage percentage',
  sample_failed: 'Sample quality test failed',
  contamination: 'Product contamination detected',
  size_mismatch: 'Sample size does not match specifications',
  quality_below_standard: 'Quality below acceptable standard',
  other: 'Other'
};

const QualityInspectionForm: React.FC<QualityInspectionFormProps> = ({
  inspectionData,
  setInspectionData,
  onApprove,
  onReject,
  isSubmitting,
  expectedQuantity
}) => {
  const handleGradeBreakdownChange = (breakdown: GradeEntry[]) => {
    setInspectionData(prev => ({
      ...prev,
      gradeBreakdown: breakdown
    }));
  };

  const isPreliminary = inspectionData.gradingType === 'preliminary';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quality Inspection Form</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Grading Type Selection */}
        <div className="space-y-3 p-4 bg-muted/50 rounded-lg border">
          <Label className="text-sm font-medium">Grading Type</Label>
          <RadioGroup
            value={inspectionData.gradingType}
            onValueChange={(value: 'preliminary' | 'final') =>
              setInspectionData(prev => ({ ...prev, gradingType: value }))
            }
            className="grid grid-cols-1 gap-3"
          >
            <div className="flex items-start space-x-3 p-3 rounded-md border bg-background">
              <RadioGroupItem value="preliminary" id="preliminary" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="preliminary" className="flex items-center gap-2 font-medium cursor-pointer">
                  <Clock className="h-4 w-4 text-amber-500" />
                  Preliminary (Sample-based)
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Quick assessment based on sample. Payment proceeds now, full grading later.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3 p-3 rounded-md border bg-background">
              <RadioGroupItem value="final" id="final" className="mt-1" />
              <div className="flex-1">
                <Label htmlFor="final" className="flex items-center gap-2 font-medium cursor-pointer">
                  <ClipboardCheck className="h-4 w-4 text-green-500" />
                  Final (Complete Grading)
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Full quality assessment completed. This is the final determination.
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>

        {/* Expected Final Grading Date - Only show for preliminary */}
        {isPreliminary && (
          <div className="space-y-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <Label htmlFor="finalGradingExpectedDate" className="text-amber-800">
              Expected Final Grading Date
            </Label>
            <Input
              id="finalGradingExpectedDate"
              type="date"
              value={inspectionData.finalGradingExpectedDate}
              onChange={(e) =>
                setInspectionData(prev => ({ ...prev, finalGradingExpectedDate: e.target.value }))
              }
              min={new Date().toISOString().split('T')[0]}
            />
            <p className="text-xs text-amber-600">
              When will the complete grading be finished? (Usually 2-3 days)
            </p>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <Checkbox
            id="sampleSizeChecked"
            checked={inspectionData.sampleSizeChecked}
            onCheckedChange={(checked) =>
              setInspectionData(prev => ({ ...prev, sampleSizeChecked: !!checked }))
            }
          />
          <Label htmlFor="sampleSizeChecked">Sample size verified and acceptable</Label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sampleSizeNotes">Sample Size Notes</Label>
          <Textarea
            id="sampleSizeNotes"
            value={inspectionData.sampleSizeNotes}
            onChange={(e) =>
              setInspectionData(prev => ({ ...prev, sampleSizeNotes: e.target.value }))
            }
            placeholder="Add notes about sample size verification..."
            rows={2}
          />
        </div>

        {/* Grade Breakdown Section */}
        <div className="pt-2 border-t">
          <GradeBreakdownInput
            gradeBreakdown={inspectionData.gradeBreakdown}
            onChange={handleGradeBreakdownChange}
            expectedQuantity={expectedQuantity}
          />
        </div>

        {/* Damage & Compensation Section */}
        <div className="pt-2 border-t space-y-4">
          <h4 className="font-medium text-sm text-muted-foreground">
            {isPreliminary ? 'Estimated Damage & Compensation (from sample)' : 'Actual Damage & Compensation'}
          </h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="damageKg">
                {isPreliminary ? 'Estimated Damage (KG)' : 'Actual Damage (KG)'}
              </Label>
              <Input
                id="damageKg"
                type="number"
                min="0"
                step="0.1"
                value={inspectionData.damageKg}
                onChange={(e) =>
                  setInspectionData(prev => ({ ...prev, damageKg: parseFloat(e.target.value) || 0 }))
                }
                placeholder="e.g. 20"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="compensationKg">
                {isPreliminary ? 'Estimated Compensation (KG)' : 'Actual Compensation (KG)'}
              </Label>
              <Input
                id="compensationKg"
                type="number"
                min="0"
                step="0.1"
                value={inspectionData.compensationKg}
                onChange={(e) =>
                  setInspectionData(prev => ({ ...prev, compensationKg: parseFloat(e.target.value) || 0 }))
                }
                placeholder="e.g. 15"
              />
            </div>
          </div>
          
          {inspectionData.compensationKg > 0 && (
            <p className="text-sm text-amber-600">
              ⚠️ {isPreliminary ? 'Estimated' : ''} compensation of {inspectionData.compensationKg} KG will be deducted from payment
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="damagePercentage">Damage Percentage (%)</Label>
          <Input
            id="damagePercentage"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={inspectionData.damagePercentage}
            onChange={(e) =>
              setInspectionData(prev => ({ ...prev, damagePercentage: parseFloat(e.target.value) || 0 }))
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="damageNotes">
            {isPreliminary ? 'Preliminary Notes' : 'Damage Notes'}
          </Label>
          <Textarea
            id="damageNotes"
            value={inspectionData.damageNotes}
            onChange={(e) =>
              setInspectionData(prev => ({ ...prev, damageNotes: e.target.value }))
            }
            placeholder={isPreliminary ? "Notes about the preliminary assessment..." : "Describe any damage observed..."}
            rows={2}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="rejectionReason">Rejection Reason (if rejecting)</Label>
          <Select
            value={inspectionData.rejectionReason}
            onValueChange={(value) =>
              setInspectionData(prev => ({ ...prev, rejectionReason: value }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select rejection reason..." />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(QUALITY_REJECTION_REASONS).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Info box for preliminary grading */}
        {isPreliminary && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
            <p className="font-medium mb-1">ℹ️ Preliminary Assessment</p>
            <p>Payment will proceed based on this sample assessment. Finance will be notified to compare with final grading when completed.</p>
          </div>
        )}

        <div className="flex gap-2 pt-4">
          <Button
            onClick={onApprove}
            disabled={isSubmitting || !inspectionData.sampleSizeChecked || (isPreliminary && !inspectionData.finalGradingExpectedDate)}
            className="flex-1"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {isPreliminary ? 'Approve (Preliminary)' : 'Approve Quality'}
          </Button>
          <Button
            variant="destructive"
            onClick={onReject}
            disabled={isSubmitting || !inspectionData.rejectionReason}
            className="flex-1"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Reject
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default QualityInspectionForm;
